/*
 |------------------------------------------------------------------------------
 | Breakpoints utility
 |------------------------------------------------------------------------------
 |
 | This utility handles UI/UX changes based on breakpoints managed
 | by the browsers native media query handling system and ports
 | this to an API usable with events and getters.
 |
 */
/* ========================================================================== *\
	Imports
/* ========================================================================== */
import { partial, isFunction } from 'lodash';

/* ========================================================================== *\
	Constants & variables
/* ========================================================================== */

/**
 * Holds the initialized state of the util
 * @type {Boolean}
 */
const breakpoints = {
        desktop: 'desktop',
        mobile: 'mobile',
        tablet: 'tablet'
    },
    /**
     * The css property to use as placeholder
     * @type {String}
     */
    cssProperty = 'font-family',
    /**
     * Change event used in this module
     * @type {String}
     */
    breakpointChangeEvent = 'breakpointChange',
    /**
     * Default fallback breakpoint
     * @type {String}
     */
    fallbackBreakpoint = breakpoints.desktop,
    /**
     * Element to check the content property on
     * @type {jQuery}
     */
    head = document.head;

/**
 * Current breakpoint
 * @type {String}
 */
let currentBreakpoint = '',
    isInitialized = false;

/* ========================================================================== *\
    Functions
/* ========================================================================== */

/**
 * A helper function that removes quotes from a string
 * @param {String} string The string to strip quotes off
 */
function removeQuotes(string) {
    return string.replace(/^['"]+|\s+|\\|(;\s?})+|['"]$/g, '');
}

/**
 * Check if the given breakpoint is valid
 * @param {String} breakpoint The name of the breakpoint
 */
function validateBreakpoint(breakpoint) {
    return breakpoint && breakpoints[breakpoint];
}

/**
 * Set the breakpoint to a the given value
 * @param {String} breakpoint The name of the breakpoint
 */
function setBreakpoint(breakpoint) {
    var oldBreakpoint = currentBreakpoint;

    currentBreakpoint = validateBreakpoint(breakpoint)
        ? breakpoint
        : fallbackBreakpoint;

    // Make sure we only trigger the event after resize, not on init
    if (
        // If the breakpoint is not the same as the previous
        oldBreakpoint !== currentBreakpoint &&
        // And the module is already initialized
        isInitialized
    ) {
        const event = new CustomEvent(breakpointChangeEvent, {
            detail: {
                currentBreakpoint,
                oldBreakpoint
            }
        });

        window.dispatchEvent(event);
    }
}

/**
 * When a breakpoint changes
 * call a specific set of function
 *
 * note that the callback also gets the new and the old breakpoint
 */
function onChange(callback) {
    if (isFunction(callback)) {
        window.addEventListener(breakpointChangeEvent, callback);
    }
}

/**
 * Update the breakpoint if a resize is finished
 */
function updateBreakpoint() {
    // Reason we strip the quotes is because of browser returning
    // inconsistent values, some wrapped in quotes and some not
    var styles = window.getComputedStyle(head),
        breakpoint = removeQuotes(styles[cssProperty]);

    setBreakpoint(breakpoint);
}

/**
 * Get the current breakpoint
 */
function get() {
    return currentBreakpoint;
}

/**
 * Check if something is a given breakpoint
 */
function isCurrent(breakpoint) {
    return get() === breakpoint;
}

/* ========================================================================== *\
    Initialization
/* ========================================================================== */

/**
 * Initialize the util
 */
function init() {
    // Update breakpoint when window gets resized
    // so we get wanted behaviour on changes
    window.addEventListener('resize', updateBreakpoint);

    updateBreakpoint();

    // Mark the util as initialized
    isInitialized = true;
}

init();

/* ========================================================================== *\
    Exports
\* ========================================================================== */

export { breakpoints, get, onChange, updateBreakpoint };
export const isDesktop = partial(isCurrent, breakpoints.desktop);
export const isMobile = partial(isCurrent, breakpoints.mobile);
export const isTablet = partial(isCurrent, breakpoints.tablet);
